import * as ko from 'knockout';

import { TRAIT_ACTION_TYPES, TraitActionOptionsData } from '../../api/trait_actions';
import { SelectedTrait } from '../../models/trial';
import { TraitAction } from '../../models/trait_action';
import i18n from '../../i18n';
import { BaseTraitActionViewModel } from './base_trait_action';

export class PictureAnalysisViewModel extends BaseTraitActionViewModel {
  i18n = i18n;
  pictureTraitLabel = i18n.t('Select a trait...');
  actionLabel = i18n.t('Select an action...');
  modelLabel = i18n.t('Select a model...');

  // TODO: move to backend
  aiModels = [
    'gemini-3.1-pro-preview',
    'gemini-3-flash-preview',
    'gemini-3.1-flash-lite-preview',
    'gemini-2.5-pro',
    'gemini-2.5-flash',
    'gemini-2.5-flash-lite',
  ];

  fgccManualEnabled = ko.observable<boolean>(false);
  fgccAiEnabled = ko.observable<boolean>(false);

  // Expose these via getters to maintain HTML compatibility
  get pictureAnalysisSwitchOn() { return this.switchOn; }
  get loadingNewPictureAnalysisAction() { return this.loadingNewTraitAction; }
  get pictureAnalysisActions() { return this.traitActions; }
  get pictureAnalysisValidationError() { return this.validationError; }

  constructor(params: {
    initialTraitActions: ko.ObservableArray<TraitAction>;
    selectedTraits: ko.ObservableArray<SelectedTrait>;
    traitActionsError?: ko.Observable<string>;
    fgccManualEnabled: boolean;
    fgccAiEnabled: boolean;
  }) {
    super(params);
    this.fgccManualEnabled(params.fgccManualEnabled);
    this.fgccAiEnabled(params.fgccAiEnabled);
  }

  protected updateIsEnabled(options: TraitActionOptionsData) {
    this.isEnabled(
      options.sources.some((s) => s.type === 'picture') &&
      options.targets.some((t) => t.type === 'integer' || t.type === 'decimal')
    );
  }

  protected onTraitOptionsRefreshed(traitAction: TraitAction, options: TraitActionOptionsData) {
    const validSourceIds = traitAction.getPictureSourceOptions().map((o) => o.id);
    if (traitAction.sourceTraitId() && !validSourceIds.includes(traitAction.sourceTraitId())) {
      traitAction.sourceTraitId(undefined);
    }

    const validTargetIds = this.getAvailableTargetOptions(traitAction).map((o) => o.id);
    if (traitAction.targetTraitId() && !validTargetIds.includes(traitAction.targetTraitId())) {
      traitAction.targetTraitId(undefined);
    }
  }

  protected onAddNewAction(newTraitAction: TraitAction) {
    newTraitAction.actionType(
      this.fgccManualEnabled()
        ? TRAIT_ACTION_TYPES.CALCULATE_CANOPY_STANDARD
        : TRAIT_ACTION_TYPES.CALCULATE_CANOPY_AI
    );
  }

  getAvailableActionTypes(traitAction: TraitAction) {
    return traitAction.getActionTypeOptions().filter((o) => {
      return (
        (o.value === TRAIT_ACTION_TYPES.CALCULATE_CANOPY_STANDARD && this.fgccManualEnabled()) ||
        (o.value === TRAIT_ACTION_TYPES.CALCULATE_CANOPY_AI && this.fgccAiEnabled())
      );
    });
  }

  getAvailableTargetOptions(currentAction: TraitAction) {
    const allSelectedTargetIds = this.traitActions()
      .filter((a) => a !== currentAction && a.targetTraitId())
      .map((a) => a.targetTraitId());

    const sourceId = currentAction.sourceTraitId();
    const sourceOption = currentAction
      .getPictureSourceOptions()
      .find((o) => o.id === sourceId);
    const sourceScheduledVisitIds = sourceOption?.scheduled_visit_ids ?? [];

    return currentAction.getNumberTargetOptions().filter((option) => {
      if (allSelectedTargetIds.includes(option.id)) {
        return false;
      }

      const targetScheduledVisitIds = option.scheduled_visit_ids;

      // Keep only targets that share at least one scheduled visit with the source trait.
      return targetScheduledVisitIds.some((id: number) =>
        sourceScheduledVisitIds.includes(id)
      );
    });
  }
}

ko.components.register('picture-analysis', {
  viewModel: PictureAnalysisViewModel,
  template: require('raw-loader!../../../templates/components/trial_wizard/picture_analysis.html').default,
});
